import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import { IntroCoverSection } from './components/ui/IntroCoverSection';
import { BookSection, BookSectionWhole } from './components/ui/BookSection';
import { ShowcaseSection } from './components/ui/ShowcaseSection';
import { FloatingNameTitle } from './components/ui/FloatingNameTitle';
import { WritingSection } from './components/ui/WritingSection';
import {
  HomeTopNav,
  type HomeLanguage,
  type HomeTopNavLabels,
} from './components/ui/HomeTopNav';
import { ContactSection } from './components/ui/ContactSection';
import { CustomPointer } from './components/ui/CustomPointer';
import {
  type LetterRevealCopy,
  type LetterRevealCoverImageMetrics,
} from './components/ui/LetterRevealSection';
import { DimensionalGateProject } from './pages/DimensionalGateProject';
import { EnsnaredProject } from './pages/EnsnaredProject';
import { NetflixProject } from './pages/NetflixProject';
import { FreshMarkProject } from './pages/FreshMarkProject';
import { UnwrapLoveProject } from './pages/UnwrapLoveProject';
import { WritingArticlePage } from './pages/WritingArticlePage';
import { AboutPage } from './pages/AboutPage';

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

const DESKTOP_MIN_WIDTH = 810;
const DESKTOP_MEDIA_QUERY = `(min-width: ${DESKTOP_MIN_WIDTH}px)`;
const PROJECT_DETAIL_PATHS = new Set([
  '/dimensional-gate',
  '/netflix-dashboard',
  '/freshmark',
  '/unwrap-love',
  '/about',
]);

const getIsDesktop = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(DESKTOP_MEDIA_QUERY).matches;

type HomeCopy = {
  intro: LetterRevealCopy;
  nav: HomeTopNavLabels;
  showcaseNote: string;
  contact: {
    eyebrow: string;
    title: string;
    email: string;
  };
};

const HOME_COPY: Record<HomeLanguage, HomeCopy> = {
  en: {
    intro: [
      [
        { text: 'hi, my name is ' },
        { text: 'mia.', isHighlight: true },
      ],
      [
        { text: "i'm a " },
        { text: 'master of it', isHighlight: true },
        { text: ' student at the ' },
        { text: 'university of auckland' },
        { text: ', with 4 years of ' },
        { text: 'creative experience in advertising.', isHighlight: true },
      ],
    ],
    nav: {
      about: 'about me',
      showcase: 'showcase',
      writing: 'writing',
      contact: 'contact',
    },
    showcaseNote: 'My Showcase',
    contact: {
      eyebrow: 'contact',
      title: "let's stay in touch.",
      email: 'xmia665@aucklanduni.ac.nz',
    },
  },
  zh: {
    intro: [
      [
        { text: '你好，我是 ' },
        { text: 'Mia。', isHighlight: true },
      ],
      [
        { text: '我是一名 ' },
        { text: 'IT 硕士', isHighlight: true },
        { text: '学生，有 4 年' },
        { text: '创意背景。', isHighlight: true },
      ],
    ],
    nav: {
      about: '关于我',
      showcase: '作品',
      writing: '文章',
      contact: '联系',
    },
    showcaseNote: '我的作品',
    contact: {
      eyebrow: '联系',
      title: '保持联系。',
      email: 'xmia665@aucklanduni.ac.nz',
    },
  },
};

function Home() {
  const [isNameVisible, setIsNameVisible] = useState(true);
  const [isHeroActive, setIsHeroActive] = useState(true);
  const [isDesktop, setIsDesktop] = useState(getIsDesktop);
  const language: HomeLanguage = 'en';
  const [coverImageMetrics, setCoverImageMetrics] = useState<LetterRevealCoverImageMetrics>({
    imageHeight: 0,
    visibleImageOffset: 0,
  });
  const lastScrollYRef = useRef(0);
  const accumulatedScrollDeltaRef = useRef(0);
  const tickingRef = useRef(false);

  const scrollToTarget = (target: number | string) => {
    gsap.to(window, {
      duration: 1,
      scrollTo: target,
      ease: 'power2.inOut',
    });
  };

  const handleNameClick = () => {
    scrollToTarget('#about-me');
  };

  const handleAboutClick = () => {
    scrollToTarget('#about-me');
  };

  const handleShowcaseClick = () => {
    scrollToTarget('#showcase');
  };

  const handleWritingClick = () => {
    scrollToTarget('#writing');
  };

  const handleContactClick = () => {
    scrollToTarget('#contact');
  };

  useEffect(() => {
    lastScrollYRef.current = window.scrollY;

    const handleScroll = () => {
      if (tickingRef.current) {
        return;
      }

      tickingRef.current = true;

      window.requestAnimationFrame(() => {
        const currentScrollY = window.scrollY;
        const delta = currentScrollY - lastScrollYRef.current;

        if (
          delta !== 0 &&
          accumulatedScrollDeltaRef.current !== 0 &&
          Math.sign(delta) !== Math.sign(accumulatedScrollDeltaRef.current)
        ) {
          accumulatedScrollDeltaRef.current = 0;
        }

        accumulatedScrollDeltaRef.current += delta;

        setIsHeroActive(currentScrollY < window.innerHeight * 0.82);

        if (currentScrollY <= 8) {
          setIsNameVisible(true);
          accumulatedScrollDeltaRef.current = 0;
        } else if (accumulatedScrollDeltaRef.current > 12) {
          setIsNameVisible(false);
          accumulatedScrollDeltaRef.current = 0;
        } else if (accumulatedScrollDeltaRef.current < -12) {
          setIsNameVisible(true);
          accumulatedScrollDeltaRef.current = 0;
        }

        lastScrollYRef.current = currentScrollY;
        tickingRef.current = false;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);

    const handleChange = (event: MediaQueryListEvent) => {
      setIsDesktop(event.matches);
    };

    setIsDesktop(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useEffect(() => {
    if (
      coverImageMetrics.imageHeight === 0 &&
      coverImageMetrics.visibleImageOffset === 0
    ) {
      return;
    }

    const refreshId = window.requestAnimationFrame(() => {
      ScrollTrigger.refresh();
    });

    return () => {
      window.cancelAnimationFrame(refreshId);
    };
  }, [coverImageMetrics]);

  const handleCoverImageMetricsChange = (
    nextMetrics: LetterRevealCoverImageMetrics
  ) => {
    setCoverImageMetrics((currentMetrics) => {
      if (
        currentMetrics.visibleImageOffset === nextMetrics.visibleImageOffset &&
        currentMetrics.imageHeight === nextMetrics.imageHeight
      ) {
        return currentMetrics;
      }

      return nextMetrics;
    });
  };

  const copy = HOME_COPY[language];

  return (
    <div className={`App${isHeroActive ? ' is-hero-active' : ''}`}>
      <FloatingNameTitle
        text="Mia Miao"
        isVisible={isNameVisible}
        onClick={handleNameClick}
      />
      <HomeTopNav
        isVisible={isNameVisible}
        labels={copy.nav}
        onAboutClick={handleAboutClick}
        onShowcaseClick={handleShowcaseClick}
        onWritingClick={handleWritingClick}
        onContactClick={handleContactClick}
      />

      <div id="about-me">
        <IntroCoverSection
          introCopy={copy.intro}
          onCoverImageMetricsChange={handleCoverImageMetricsChange}
          desktopBottomSection={
            isDesktop ? (
              <div id="more-about-me">
                <BookSectionWhole />
              </div>
            ) : undefined
          }
        />
      </div>

      <div id="showcase">
        <ShowcaseSection note={copy.showcaseNote} />
      </div>
      {isDesktop ? null : (
        <div id="more-about-me">
          <BookSection
            imageHeight={coverImageMetrics.imageHeight}
            visibleImageOffset={coverImageMetrics.visibleImageOffset}
          />
        </div>
      )}
      <WritingSection language={language} />
      <div id="contact">
        <ContactSection {...copy.contact} />
      </div>
    </div>
  );
}

export function DetailRouteScrollToTop() {
  const { pathname } = useLocation();
  const previousPathnameRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (
      (PROJECT_DETAIL_PATHS.has(pathname) || pathname.startsWith('/writing/')) &&
      previousPathnameRef.current !== pathname
    ) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }

    previousPathnameRef.current = pathname;
  }, [pathname]);

  return null;
}

function App() {
  return (
    <>
      <CustomPointer />
      <DetailRouteScrollToTop />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/dimensional-gate" element={<DimensionalGateProject />} />
        <Route path="/netflix-dashboard" element={<NetflixProject />} />
        <Route path="/freshmark" element={<FreshMarkProject />} />
        <Route path="/unwrap-love" element={<UnwrapLoveProject />} />
        <Route path="/ensnared" element={<EnsnaredProject />} />
        <Route path="/writing/:articleSlug" element={<WritingArticlePage />} />
      </Routes>
    </>
  );
}

export default App;
